<script setup lang="ts">
  import type { FormSubmitEvent, Form } from "#ui/types";
  import { signinSchema } from "~~/validators/auth";
  import constants from "~~/constants";
  import type { z } from "zod";

  const { mode } = defineProps<{
    mode: "signin" | "submissionLock";
  }>();

  const emits = defineEmits<{
    (e: "unlocked"): void;
  }>();

  const schema = signinSchema();
  type Schema = z.output<typeof schema>;

  const state = reactive<Partial<Schema>>({
    email: "",
    password: "",
  });

  const isSubmitting = ref(false);
  const errorMessage = ref("");

  const showPassword = ref(false);
  const { t } = useI18n();

  const { post } = apiRepository();
  const userStore = useUserStore();
  const organizationStore = useOrganizationStore();
  const intendedOrganization = useRoute().query.organization as
    | string
    | undefined;

  async function onSubmit(event: FormSubmitEvent<Schema>) {
    isSubmitting.value = true;

    const { data, success, status, message } = await post<{
      token: string;
      refreshToken: string;
    }>(mode == "signin" ? "/auth/signin" : "/auth/unlock-user", {
      ...event.data,
      email: event.data.email?.toLowerCase(),
      organizationUrl: intendedOrganization,
    });

    if (mode == "submissionLock") {
      if (success && data) {
        emits("unlocked");
        return;
      }
      isSubmitting.value = false;
      if (status == constants.STATUS_NOT_FOUND) {
        errorMessage.value = t("errors.accountNotFound");
      } else {
        throw createError({
          statusCode: status,
          statusMessage: message,
        });
      }
    } else {
      if (success && data) {
        userStore.setAuthToken(data.token, data.refreshToken);
        await userStore.fetchUserData();
        navigateTo(`/${organizationStore.organizationUrl}`);
        return;
      }
      isSubmitting.value = false;

      if (status == constants.STATUS_UNAUTHORIZED) {
        errorMessage.value = message;
      } else if (status == constants.STATUS_NOT_FOUND) {
        errorMessage.value = t("errors.accountNotFound");
      } else {
        throw createError({
          statusCode: status,
          statusMessage: message,
        });
      }
    }
  }

  const formRef = useTemplateRef<Form<Schema>>("form");

  const formHasErrors = computed(() => {
    return (
      formRef.value &&
      formRef.value.errors.value &&
      formRef.value.errors.value.length > 0
    );
  });
</script>

<template>
  <UForm
    :schema="schema"
    :state="state"
    class="space-y-6 p-6"
    ref="form"
    @submit="onSubmit"
    :validate-on="['input']"
  >
    <div>
      <h1 class="text-center text-2xl font-bold text-gray-900">
        {{ $t("auth.signin.heading") }}
      </h1>
      <p class="text-center text-gray-500">
        {{
          mode == "signin"
            ? $t("auth.signin.form-message")
            : $t("auth.signin.form-submission-lock-message")
        }}
      </p>
    </div>
    <section class="space-y-5">
      <UFormField
        :label="$t('labels.email')"
        name="email"
        required
        :ui="{
          root: 'relative',
          container: 'static',
          error: 'absolute top-full text-xs mt-0',
        }"
      >
        <UInput
          v-model="state.email"
          placeholder="example@example.com"
        />
      </UFormField>
      <UFormField
        :label="$t('labels.password')"
        name="password"
        required
        :ui="{
          root: 'relative',
          container: 'static',
          error: 'absolute top-full text-xs mt-0',
        }"
      >
        <UInput
          v-model="state.password"
          :type="showPassword ? 'text' : 'password'"
          placeholder="********"
        >
          <template #trailing>
            <UButton
              color="neutral"
              variant="ghost"
              class="border-0 p-0"
              :icon="
                showPassword
                  ? 'i-heroicons:eye-solid'
                  : 'i-heroicons:eye-slash-solid'
              "
              :aria-label="
                showPassword
                  ? $t('actions.hidePassword')
                  : $t('actions.showPassword')
              "
              :aria-pressed="showPassword"
              aria-controls="password"
              @click="showPassword = !showPassword"
              type="button"
            />
          </template>
        </UInput>
        <UButton
          v-if="mode == 'signin'"
          to="/forgot-password"
          class="float-right my-2 hover:underline rtl:float-left"
          variant="ghost"
        >
          {{ $t("actions.forgotPassword") }}
        </UButton>
        <p
          v-if="errorMessage && !formHasErrors"
          class="absolute text-(--ui-error)"
        >
          {{ errorMessage }}
        </p>
      </UFormField>
    </section>
    <UButton type="submit" :loading="isSubmitting">
      {{ $t("actions.signin") }}
    </UButton>
    <p v-if="mode == 'signin'" class="text-center text-gray-500">
      {{ $t("shared.terms&conditions") }}
      <UButton
        to="/"
        type="button"
        variant="ghost"
        class="px-0 font-bold hover:underline"
      >
        {{ $t("shared.terms&conditions-link") }}.
      </UButton>
    </p>
  </UForm>
</template>

<style scoped></style>
